import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { captureElements, copyBlobToClipboard, downloadBlob, shareImageFile, CaptureOptions } from "../utils/shareLeague";

export type ExportStatus = { type: "success" | "error"; message: string } | null;

/**
 * Drives the capture → share/copy/download flow and surfaces a transient
 * status message. Without this, a successful clipboard copy or a failed
 * capture both look identical to the user on mobile: the spinner stops and
 * nothing visibly happens.
 */
export function useExportImage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ExportStatus>(null);
  const [loading, setLoading] = useState(false);
  const lastExportRef = useRef(0);
  const statusTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const showStatus = (s: ExportStatus) => {
    clearTimeout(statusTimerRef.current);
    setStatus(s);
    statusTimerRef.current = setTimeout(() => setStatus(null), 3000);
  };

  const runExport = async (els: (HTMLElement | null)[], action: "download" | "copy", filename: string, captureOptions?: CaptureOptions) => {
    if (Date.now() - lastExportRef.current < 3000) return;
    const nonNullEls = els.filter((el): el is HTMLElement => el !== null);
    if (!nonNullEls.length) return;
    lastExportRef.current = Date.now();
    setLoading(true);
    try {
      const blob = await captureElements(nonNullEls, captureOptions);
      if (action === "copy") {
        if (await copyBlobToClipboard(blob)) {
          showStatus({ type: "success", message: t("export.copied") });
        } else if (await shareImageFile(blob, filename, captureOptions?.joinUrl)) {
          // share sheet handled it (mobile fallback when clipboard API unavailable)
        } else {
          downloadBlob(blob, filename);
          showStatus({ type: "success", message: t("export.downloaded") });
        }
      } else {
        downloadBlob(blob, filename);
        showStatus({ type: "success", message: t("export.downloaded") });
      }
    } catch (err) {
      console.error("Export failed:", err);
      showStatus({ type: "error", message: t("export.failed") });
    } finally {
      setLoading(false);
    }
  };

  return { status, loading, runExport };
}
